<template>
  <div class="app-button-box">
    <!-- 自定义按钮插槽 -->
    <slot
      :loading="openAppLoading"
      :handle-open-app="handleOpenApp"
    >
      <!-- 默认按钮样式 -->
      <PPButton
        :loading="openAppLoading"
        class="default-open-app-btn"
        height="36px"
        type="primary"
        @click="handleOpenApp"
      >
        {{ buttonText || $t('open-app') }}
      </PPButton>
    </slot>
  </div>

  <!-- 引导弹窗 -->
  <ClientOnly>
    <AsyncGuidePopup
      ref="guidePopupRef"
      @to-app="handleToApp"
    />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, isPikPakClient, ref, useCookie } from '#imports'
import PPButton from '@/components/PPButton.vue'
import { shareTrackParams } from '@/components/share/share.script'
import useShareId from '@/composables/useShareId'
import track from '@/track/webShare'
import toApp from '@/utils/toApp'
import { StorageKey } from '~/types/enum'

const AsyncGuidePopup = defineAsyncComponent(() => import('~/layouts/drive/GuidePopup.vue'))

interface Props {
  /** 按钮文本，默认为 "打开 APP" */
  buttonText?: string
  /** 是否在客户端内隐藏按钮，默认为 true */
  hideInClient?: boolean
  /** 自定义分享ID，默认使用当前页面的分享ID */
  customShareId?: string
  /** 自定义埋点参数 */
  customTrackParams?: Record<string, string | number | boolean>
}

interface Emits {
  /** 点击打开APP按钮时触发 */
  (e: 'click'): void
  /** 成功跳转到APP时触发 */
  (e: 'success'): void
  /** 显示引导弹窗时触发 */
  (e: 'show-guide'): void
}

const props = withDefaults(defineProps<Props>(), {
  buttonText: '',
  hideInClient: true,
  customShareId: '', // 自定义分享ID
  customTrackParams: () => ({}), // 自定义埋点参数
  guideHover: true, // 是否显示 hover 效果
})

const emits = defineEmits<Emits>()

const shareId = useShareId()
const openAppLoading = ref(false)
const guidePopupRef = ref<typeof AsyncGuidePopup>()

// 是否显示按钮
const visible = computed(() => {
  if (props.hideInClient) {
    return !isPikPakClient()
  }
  return true
})

// 获取实际使用的分享ID
const actualShareId = computed(() => props.customShareId || shareId.value)

// 获取实际使用的密码
const getPassword = () => useCookie(`${StorageKey.SHARE_PWD_CODE}_${actualShareId.value}`).value || ''

const handleToApp = () => {
  // 从 cookie 中获取密码
  const pwdCode = getPassword()
  // 跳转至APP
  window.location.href = toApp.share(actualShareId.value, pwdCode)
  emits('success')
}

const handleOpenApp = () => {
  emits('click')

  // 立即尝试打开APP
  handleToApp()

  // 显示加载状态
  openAppLoading.value = true

  // 埋点上报
  track.web_share_page_click({
    ...shareTrackParams(),
    button: 'open_app',
    ...props.customTrackParams,
  })

  // 1秒后显示引导弹窗
  setTimeout(() => {
    openAppLoading.value = false
    guidePopupRef.value?.show()
    emits('show-guide')
  }, 1000)
}

// 暴露方法给父组件
defineExpose({
  handleOpenApp,
  handleToApp,
  visible,
})
</script>

<style lang="less" scoped>
.app-button-box {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}

.default-open-app-btn {
  height: 36px;
}
</style>
